import { useMemo, useState } from "react";
import { ApolloError } from "@apollo/client";
import { Alert, Box, Button, Flex, Heading } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useBrandLazyQuery } from "utils/apollo/hooks/brand";
import { getLayout } from "components/Layout";
import PageSection from "components/Layout/PageSection";
import {
  existingQuery,
  getExistingCounts,
  makeValidateDefaultCustomers,
  usePerformImport,
} from "components/Customers/Import/utils";
import {
  ImportWorkSheetDefinitions,
  IMPORT_WORKSHEET_DEFINITIONS,
} from "utils/xlsxDefinitions/customers";
import CurrentFileLabeledValue from "components/Shared/CurrentFileLabeledValue";
import HeadTitle from "components/Shared/HeadTitle";
import StatusLabeledValue from "components/Shared/StatusLabeledValue";
import useXlsxImportUpload, {
  ProcessingCompleteData,
} from "utils/xlsxImportExport/useXlsxImportUpload";
import useXlsxImportUploadDropzone from "utils/xlsxImportExport/useXlsxImportUploadDropzone";
import XlsxImportErrorAlert from "components/Shared/XlsxImportExport/ErrorAlert";
import XlsxImportUploadPanel from "components/Shared/XlsxImportExport/UploadPanel";
import CountsSection from "components/Shared/XlsxImportExport/CountsSection";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { makeColumnUniquenessValidation } from "utils/xlsxImportExport/validations";
import { CUSTOMERS_INDEX } from "utils/links";
import asyncVoidHandler from "utils/asyncVoidHandler";
import useValidatedData from "utils/xlsxImportExport/useValidatedData";
import UserError from "utils/UserError";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import ContactEmailLink from "components/Shared/ContactEmailLink";

// eslint-disable-next-line @typescript-eslint/naming-convention
const IS_DISABLED = true;

export default function CustomersImport() {
  const brandId = useCurrentBrandId();
  const [processedData, setProcessedData] =
    useState<ProcessingCompleteData<ImportWorkSheetDefinitions>>();

  const [
    performExistingFetch,
    { data: existingData, loading: isLoadingExisting },
  ] = useBrandLazyQuery(existingQuery, {
    variables: brandId ? { brandId } : undefined,
    fetchPolicy: "network-only",
    nextFetchPolicy: "standby",
  });

  const { file, isProcessing, error, handleFileChange, reset } =
    useXlsxImportUpload({
      onProcessingComplete: (data) => {
        setProcessedData(data);
      },
      workSheetDefinitions: IMPORT_WORKSHEET_DEFINITIONS,
      validations: [
        makeColumnUniquenessValidation("customers", "name"),
        makeColumnUniquenessValidation("warehouses", "name", {
          scope: ["customerName"],
        }),
        makeColumnUniquenessValidation("warehouses", "code", {
          scope: ["customerName"],
        }),
        makeValidateDefaultCustomers(performExistingFetch),
      ],
    });

  const dropzone = useXlsxImportUploadDropzone({
    onFileChange: handleFileChange,
    disabled: IS_DISABLED,
  });

  const { validatedData } = useValidatedData(processedData?.xlsxImportWorkBook);

  const [performImport, { isLoading: isImporting }] = usePerformImport();

  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast({
    networkStatusMessages: {
      413:
        "Import contains too many customers and/or warehouses. Please " +
        "try splitting your import file into multiple smaller files.",
    },
  });
  const { push } = useRouter();

  const resetImport = () => {
    reset();
    setProcessedData(undefined);
  };

  const handleImport = async () => {
    if (!validatedData || !existingData)
      throw new Error("Not ready to import!");
    try {
      await performImport(validatedData, existingData);
      resetImport();
      showSuccessToast("Customers information imported successfully!");
      await push(CUSTOMERS_INDEX);
    } catch (e) {
      if (e instanceof ApolloError || e instanceof UserError) {
        showErrorToast(e, {
          title: "An error occurred during import",
          duration: null,
          isClosable: true,
        });
        resetImport();
      } else {
        throw e;
      }
    }
  };

  const existingCounts = useMemo(
    () =>
      validatedData &&
      existingData &&
      getExistingCounts(validatedData, existingData),
    [validatedData, existingData],
  );

  return (
    <PageSection>
      <HeadTitle title="Import Customers" />

      <Heading as="h1" variant="section-header" mb={5}>
        Import Customers
      </Heading>

      {IS_DISABLED && (
        <Alert status="error" display="block" variant="left-accent" my={10}>
          The customers import feature is currently unavailable. Please contact
          us at <ContactEmailLink /> for assistance.
        </Alert>
      )}

      {!file && (
        <XlsxImportUploadPanel
          dropzone={dropzone}
          isDisabled={IS_DISABLED}
          templateDownloadFileName="Customers Import Template.xlsx"
          workSheetDefinitions={IMPORT_WORKSHEET_DEFINITIONS}
        />
      )}

      {error && <XlsxImportErrorAlert error={error} my={5} />}

      {file && !IS_DISABLED && (
        <>
          <Flex mx={-2} flexWrap="wrap" sx={{ "> *": { m: 2, minW: "7em" } }}>
            <CurrentFileLabeledValue
              fileName={file?.name}
              onReset={() => {
                reset();
                setProcessedData(undefined);
              }}
            />

            <StatusLabeledValue
              isLoading={isProcessing}
              loadingText="Processing"
              status={(error && "error") || (validatedData && "success")}
            />
          </Flex>

          <CountsSection
            pluralLabel="customers"
            numRows={existingCounts?.customerNames.length}
            numExisting={existingCounts?.existingCustomerNames?.length}
            isLoading={isProcessing}
            isLoadingExisting={isLoadingExisting}
          />

          <CountsSection
            pluralLabel="warehouses"
            numRows={validatedData?.warehouses?.length}
            numExisting={existingCounts?.existingWarehouses?.length}
            isLoading={isProcessing}
            isLoadingExisting={isLoadingExisting}
          />

          <Box textAlign="right" mt={10}>
            <Button
              type="button"
              colorScheme="pinkButton"
              onClick={asyncVoidHandler(handleImport)}
              isLoading={isImporting}
              isDisabled={!validatedData || !existingData}
            >
              Import
            </Button>
          </Box>
        </>
      )}
    </PageSection>
  );
}

CustomersImport.getLayout = getLayout;
